import React, { useEffect, useState } from 'react';

export const BRAND = '#7E5FFE';
export const FONTS = "'Geist', 'Inter', ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif";
export const TITLE_FONT = "'Clesmont', 'Space Grotesk', " + FONTS;

export function usePublicFormFonts() {
  useEffect(() => {
    const id = 'si3-public-form-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

export function isReferralField(field) {
  return /referral/i.test(field?.name || '') || /referral/i.test(field?.label || '');
}

export function isSpeakerForm(form) {
  return /speaker/i.test(`${form?.title || ''} ${form?.slug || ''}`);
}

export function isInquiryForm(form) {
  return /inquir/i.test(form?.title || '') || /well-?being/i.test(form?.title || '') || /program/i.test(form?.title || '');
}

export function defaultSuccessSteps(form) {
  if (isInquiryForm(form)) {
    return [
      { title: 'We review your inquiry', detail: 'A member of our team will get back to you soon.' },
      { title: 'We follow up', detail: 'You’ll hear from us by email if there’s a fit.' },
      { title: 'Next steps', detail: 'Program details come in that follow-up.' }
    ];
  }
  return [
    { title: 'We review your application', detail: 'Within 2–3 business days.' },
    { title: 'You get your invite + onboarding', detail: 'Follow SI<3> instructions.' },
    { title: 'Cohort begins', detail: 'Dates arrive with your invite.' }
  ];
}

export function formSuccessSteps(form) {
  const saved = Array.isArray(form?.successSteps)
    ? form.successSteps.filter(step => step && (step.title || step.detail))
    : [];
  const source = saved.length ? saved.slice(0, 3) : defaultSuccessSteps(form);
  return source.map((step, index) => ({
    n: String(index + 1).padStart(2, '0'),
    title: step.title || '',
    detail: step.detail || ''
  }));
}

export function submitLabel(form) {
  if (form?.submitLabel) return form.submitLabel;
  if (isSpeakerForm(form)) return 'Submit Info';
  return isInquiryForm(form) ? 'Submit Inquiry' : 'Submit Application';
}

export function receivedLabel(form) {
  if (form?.receivedTitle) return form.receivedTitle;
  if (isSpeakerForm(form)) return 'SUBMISSION RECEIVED';
  return isInquiryForm(form) ? 'INQUIRY RECEIVED' : 'APPLICATION RECEIVED';
}

export function thankYouMid(form) {
  if (form?.thankYouMessage) return form.thankYouMessage;
  if (isSpeakerForm(form)) return 'your submission is in.';
  return `your ${form?.title || 'response'} is in.`;
}

function shareLinkLabel(form) {
  const hay = `${form?.title || ''} ${form?.slug || ''}`.toLowerCase();
  if (hay.includes('defi')) return 'Share Si Her DeFi with your friends and colleagues!';
  if (hay.includes('dao')) return 'Share Si Her DAO with your friends and colleagues!';
  if (hay.includes('grow3dge')) return 'Share Grow3dge with your friends and colleagues!';
  if (hay.includes('well')) return 'Share Well-Being with your friends and colleagues!';
  return 'Share with your friends and colleagues!';
}

function norm(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function isNameField(field) {
  return /^(full )?name$/.test(norm(field.name)) || /^(full )?name$/.test(norm(field.label));
}

function isEmailField(field) {
  return field.type === 'email' || /email/.test(norm(field.name)) || /email/.test(norm(field.label));
}

function isOrgField(field) {
  return /org|company|organization/.test(norm(field.name) + ' ' + norm(field.label));
}

function isRoleField(field) {
  return /\brole\b/.test(norm(field.name) + ' ' + norm(field.label));
}

function isSocialField(field) {
  return /linkedin|farcaster|twitter|\bx\b|handle/.test(norm(field.name) + ' ' + norm(field.label));
}

function isYesNo(field) {
  const options = (field.options || []).map(opt => norm(opt));
  return options.length === 2 && options.includes('yes') && options.includes('no');
}

function shortOptions(field) {
  const options = field.options || [];
  return options.length >= 2 && options.length <= 6 && options.every(opt => String(opt).length <= 28);
}

export function groupFields(fields) {
  const visible = (fields || []).filter(field => !isReferralField(field));
  const groups = [];
  for (let i = 0; i < visible.length; ) {
    const a = visible[i];
    const b = visible[i + 1];
    const c = visible[i + 2];
    if (a && b && c && isSocialField(a) && isSocialField(b) && isSocialField(c)) {
      groups.push({ cols: 3, fields: [a, b, c] });
      i += 3;
      continue;
    }
    if (a && b && ((isNameField(a) && isEmailField(b)) || (isOrgField(a) && isRoleField(b)))) {
      groups.push({ cols: 2, fields: [a, b] });
      i += 2;
      continue;
    }
    groups.push({ cols: 1, fields: [a] });
    i += 1;
  }
  return groups;
}

export function shade(hex, amount) {
  const raw = String(hex || BRAND).replace('#', '');
  const full = raw.length === 3 ? raw.split('').map(ch => ch + ch).join('') : raw;
  const value = parseInt(full, 16);
  if (Number.isNaN(value)) return BRAND;
  const clamp = n => Math.max(0, Math.min(255, n));
  const r = clamp(((value >> 16) & 255) + amount);
  const g = clamp(((value >> 8) & 255) + amount);
  const b = clamp((value & 255) + amount);
  return `#${[r, g, b].map(n => n.toString(16).padStart(2, '0')).join('')}`;
}

function YesNoToggle({ field, value, onChange, disabled }) {
  const current = Array.isArray(value) ? value[0] : value;
  return (
    <div style={styles.toggle}>
      {(field.options || ['Yes', 'No']).map(option => {
        const selected = norm(current) === norm(option);
        return (
          <button
            key={option}
            type="button"
            disabled={disabled}
            onClick={() => onChange(field.name, field.type === 'checkbox' ? [option] : option)}
            style={togglePill(selected)}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

function CheckboxChip({ option, checked, disabled, onClick }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} style={chipStyle(checked)}>
      <span style={checkBox(checked)}>{checked ? '✓' : ''}</span>
      <span>{option}</span>
    </button>
  );
}

function ImageField({ field, value, onChange, disabled, uploadUrl }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputId = 'up-' + (field.id || field.name);
  const accept = field.type === 'file' ? undefined : 'image/*';

  const pick = async (file) => {
    if (!file || !uploadUrl) return;
    setBusy(true);
    setErr('');
    try {
      const body = new FormData();
      body.append('image', file);
      const response = await fetch(uploadUrl, { method: 'POST', body });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.url) throw new Error(payload.error || 'Upload failed');
      onChange(field.name, payload.url);
    } catch (error) {
      setErr(error.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {value && field.type !== 'file' ? (
        <img src={value} alt="" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
      ) : null}
      {value && field.type === 'file' ? (
        <div style={{ fontSize: 12, color: '#BBF7D0', marginBottom: 8, wordBreak: 'break-all' }}>Uploaded</div>
      ) : null}
      <button
        type="button"
        disabled={disabled || busy || !uploadUrl}
        onClick={() => document.getElementById(inputId)?.click()}
        style={styles.uploadBtn}
      >
        {busy ? 'Uploading…' : (value ? 'Replace file' : 'Upload file')}
      </button>
      <input
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled || busy}
        style={{ display: 'none' }}
        onChange={e => pick(e.target.files?.[0])}
      />
      {err ? <div style={styles.errorText}>{err}</div> : null}
      {field.required ? (
        <input tabIndex={-1} required value={value || ''} onChange={() => {}} style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
      ) : null}
    </div>
  );
}

export function FieldControl({ field, value, onChange, brandColor, disabled, uploadUrl }) {
  const common = {
    id: field.id || field.name,
    name: field.name,
    required: Boolean(field.required),
    disabled
  };

  if (field.type === 'textarea') {
    return (
      <textarea
        {...common}
        rows={4}
        value={value || ''}
        onChange={e => onChange(field.name, e.target.value)}
        placeholder={field.placeholder || 'Type details here'}
        style={{ ...styles.input, ...styles.textarea }}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <select {...common} value={value || ''} onChange={e => onChange(field.name, e.target.value)} style={{ ...styles.input, color: value ? '#FFFFFF' : '#8C8799' }}>
        <option value="">{field.placeholder || 'Select…'}</option>
        {(field.options || []).map(option => <option key={option} value={option}>{option}</option>)}
      </select>
    );
  }

  if ((field.type === 'radio' || field.type === 'checkbox') && isYesNo(field)) {
    return <YesNoToggle field={field} value={value} onChange={onChange} disabled={disabled} />;
  }

  if (field.type === 'radio' && shortOptions(field)) {
    return (
      <div style={styles.pillRow}>
        {(field.options || []).map(option => {
          const selected = value === option;
          return (
            <button key={option} type="button" disabled={disabled} onClick={() => onChange(field.name, option)} style={togglePill(selected)}>
              {option}
            </button>
          );
        })}
      </div>
    );
  }

  if (field.type === 'checkbox' && (field.options || []).length) {
    const selected = Array.isArray(value) ? value : [];
    const grid = field.options.length >= 4;
    return (
      <div style={grid ? styles.choiceGrid : styles.pillRow} className={grid ? 'si3-public-choice-grid' : undefined}>
        {field.options.map(option => {
          const checked = selected.includes(option);
          return (
            <CheckboxChip
              key={option}
              option={option}
              checked={checked}
              disabled={disabled}
              onClick={() => onChange(field.name, checked ? selected.filter(item => item !== option) : [...selected, option])}
            />
          );
        })}
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label style={styles.choice}>
        <input {...common} type="checkbox" checked={Boolean(value)} onChange={e => onChange(field.name, e.target.checked)} />
        <span>{field.placeholder || field.label}</span>
      </label>
    );
  }

  if (field.type === 'image' || field.type === 'file') {
    return <ImageField field={field} value={value} onChange={onChange} disabled={disabled} uploadUrl={uploadUrl} />;
  }

  const type = ['email', 'number', 'date'].includes(field.type) ? field.type : 'text';
  return <input {...common} type={type} value={value || ''} onChange={e => onChange(field.name, e.target.value)} placeholder={field.placeholder || ''} style={styles.input} />;
}

export function FieldBlock({ field, value, onChange, brandColor, disabled, uploadUrl }) {
  const needsNativeRequired = field.required && (field.type === 'radio' || (field.type === 'checkbox' && (field.options || []).length));
  return (
    <div style={{ minWidth: 0, position: 'relative', width: '100%' }}>
      <label htmlFor={field.id || field.name} style={styles.label}>
        {field.label}{field.required ? <span style={{ color: '#B1A0FE' }}> *</span> : null}
      </label>
      <FieldControl field={field} value={value} onChange={onChange} brandColor={brandColor} disabled={disabled} uploadUrl={uploadUrl} />
      {needsNativeRequired ? (
        <input
          tabIndex={-1}
          required
          value={Array.isArray(value) ? value.join(',') : (value || '')}
          onChange={() => {}}
          aria-hidden="true"
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
        />
      ) : null}
    </div>
  );
}

export function FieldsLayout({ fields, values, onChange, brandColor, disabled, uploadUrl }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%' }}>
      {groupFields(fields).map((group, index) => (
        <div
          key={group.fields.map(field => field.id || field.name).join('-') || index}
          className={group.cols > 1 ? 'si3-public-field-row' : undefined}
          style={{
            display: 'grid',
            gridTemplateColumns: group.cols === 1 ? '1fr' : `repeat(${group.cols}, minmax(0, 1fr))`,
            gap: 17
          }}
        >
          {group.fields.map(field => (
            <FieldBlock
              key={field.id || field.name}
              field={field}
              value={values[field.name]}
              onChange={onChange}
              brandColor={brandColor}
              disabled={disabled}
              uploadUrl={uploadUrl}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ReferralBlock({ referral, onCodeChange, onApply, onRemove, brandColor, disabled }) {
  const invalid = Boolean(referral.message) && !referral.valid;

  if (referral.valid) {
    return (
      <div style={styles.referralRow}>
        <div style={{ ...styles.label, marginBottom: 0, flex: 1 }}>Have a referral code? Add it here so your referrer gets credit.</div>
        <div style={styles.appliedBox}>
          <div style={{ fontWeight: 600, color: '#86EFAC', fontSize: 12 }}>Referral Applied</div>
          <button type="button" onClick={onRemove} disabled={disabled} style={styles.removeBtn}>Remove</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.referralRow}>
      <div style={{ ...styles.label, marginBottom: 0, flex: 1 }}>Have a referral code? Add it here so your referrer gets credit.</div>
      <div style={styles.referralControl}>
        <input
          value={referral.code || ''}
          onChange={e => onCodeChange(e.target.value.toUpperCase().replace(/\s+/g, ''))}
          placeholder="Enter code"
          disabled={disabled}
          style={{
            ...styles.input,
            width: 188,
            minHeight: 38,
            borderColor: invalid ? '#EF4444' : 'rgba(255,255,255,0.1)'
          }}
        />
        <button
          type="button"
          onClick={onApply}
          disabled={disabled || referral.applying || String(referral.code || '').length < 4}
          style={styles.applyBtn}
        >
          {referral.applying ? '…' : 'Apply'}
        </button>
      </div>
      {invalid ? (
        <div style={{ ...styles.errorText, width: '100%' }}>This code is not active. Please double-check it.</div>
      ) : null}
    </div>
  );
}

export function ModalShell({ children, title, description, image, onClose, preview }) {
  return (
    <section style={styles.modal} onClick={event => event.stopPropagation()}>
      <div style={styles.modalHead}>
        <div style={styles.iconText}>
          <div style={styles.iconWrap}>
            {image ? <img src={image} alt="" style={styles.iconImg} /> : <span style={styles.iconGlyph} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title ? <h1 style={styles.title}>{title}</h1> : null}
            {description ? <p style={styles.description}>{description}</p> : null}
          </div>
        </div>
        {onClose ? (
          <button type="button" aria-label="Close" onClick={onClose} style={styles.closeBtn}>×</button>
        ) : preview ? (
          <span style={styles.previewTag}>Preview</span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function ThankYou({ form, values, referral, publicUrl, copied, onCopy, onDone, emailSent }) {
  const firstName = String(values.your_name || values.full_name || values.name || values.Name || values.first_name || '').trim().split(/\s+/)[0];
  const email = String(values.email || values.work_email || values.Email || '').trim();
  const steps = formSuccessSteps(form);
  const showConfirm = emailSent === true && form?.showConfirmCopy !== false && !isSpeakerForm(form);
  const showShare = form?.showShareLink !== false && !isSpeakerForm(form);

  return (
    <div>
      <div style={styles.successIcon}>✓</div>
      <h1 style={styles.title}>{receivedLabel(form)}</h1>
      <p style={{ ...styles.description, marginTop: 8 }}>
        Thanks{firstName ? `, ${firstName}` : ''} — {thankYouMid(form)}
        {showConfirm && email ? ` We’ve sent a confirmation to ${email}. If it isn’t there in a few minutes, check your spam folder.` : ''}
      </p>
      {referral?.valid ? (
        <div style={styles.referredBy}>Referred by {referral.partnerName || referral.code}</div>
      ) : null}
      <div style={styles.steps} className="si3-public-steps">
        {steps.map(step => (
          <div key={step.n} style={styles.step}>
            <div style={styles.stepNum}>{step.n}</div>
            <div style={{ fontWeight: 600, color: '#F8FAFC' }}>{step.title}</div>
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>{step.detail}</div>
          </div>
        ))}
      </div>
      {showShare ? (
      <div style={styles.shareBox}>
        <div>
          <div style={{ fontSize: 12, color: '#C4B5FD', marginBottom: 4 }}>{shareLinkLabel(form)}</div>
          <div style={{ wordBreak: 'break-all', fontSize: 13 }}>{publicUrl}</div>
        </div>
        <button type="button" onClick={onCopy} style={styles.copyBtn}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      ) : null}
      <button type="button" onClick={onDone} style={styles.doneBtn}>Done</button>
    </div>
  );
}

export function PageBackdrop({ children, onClose, variant = 'page' }) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={variant === 'overlay' ? styles.overlay : { ...styles.overlay, ...styles.pageFallback }}
    >
      <style>{`
        @media (max-width: 700px) {
          .si3-public-field-row, .si3-public-choice-grid, .si3-public-steps {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      {children}
    </div>
  );
}

function togglePill(selected) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 46,
    height: 28,
    padding: '0 15px',
    border: 0,
    background: selected ? BRAND : 'transparent',
    color: selected ? '#FFFFFF' : '#A6A1B3',
    borderRadius: 999,
    font: 'inherit',
    fontSize: 11,
    fontWeight: 500,
    cursor: 'pointer'
  };
}

function chipStyle(selected) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    minHeight: 38,
    padding: '11px 14px',
    background: selected ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.04)',
    border: `0.86px solid ${selected ? '#5635C0' : 'rgba(255,255,255,0.08)'}`,
    borderRadius: 8.5,
    color: '#E0DEEB',
    font: 'inherit',
    fontSize: 12,
    fontWeight: 400,
    cursor: 'pointer',
    textAlign: 'left'
  };
}

function checkBox(selected) {
  return {
    width: 15,
    height: 15,
    flex: '0 0 15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    background: selected ? BRAND : 'transparent',
    border: selected ? 0 : '1.1px solid rgba(255,255,255,0.25)',
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1
  };
}

export const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    boxSizing: 'border-box',
    padding: '24px 16px',
    background: 'rgba(0,0,0,0.6)',
    color: '#FFFFFF',
    fontFamily: FONTS,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto'
  },
  pageFallback: {
    background: 'rgba(12,10,24,0.92)'
  },
  modal: {
    width: 'min(824px, 100%)',
    maxHeight: 'calc(100vh - 48px)',
    overflow: 'auto',
    boxSizing: 'border-box',
    padding: '38px 31px',
    background: '#0E0B16',
    border: '0.86px solid rgba(255,255,255,0.08)',
    borderRadius: 20.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 26
  },
  modalHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 17,
    width: '100%'
  },
  iconText: {
    display: 'flex',
    alignItems: 'center',
    gap: 17,
    flex: 1,
    minWidth: 0
  },
  iconWrap: {
    width: 66,
    height: 66,
    borderRadius: 5,
    overflow: 'hidden',
    flex: '0 0 66px',
    background: 'linear-gradient(135deg, #4323AD 14.29%, #261359 85.71%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  iconImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  iconGlyph: {
    width: 28,
    height: 18,
    border: '1.5px solid #FFFFFF',
    borderRadius: '50% 50% 40% 40%',
    display: 'block'
  },
  closeBtn: {
    width: 27,
    height: 27,
    flex: '0 0 27px',
    border: '0.86px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#FFFFFF',
    borderRadius: 999,
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer'
  },
  previewTag: {
    fontSize: 11,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: '#94A3B8'
  },
  title: {
    margin: 0,
    fontFamily: TITLE_FONT,
    fontSize: 23,
    lineHeight: 1,
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: 'uppercase',
    color: '#FFFFFF'
  },
  description: {
    margin: '7px 0 0',
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 1.45,
    fontSize: 12.4,
    fontWeight: 400,
    maxWidth: 500
  },
  label: {
    display: 'block',
    marginBottom: 7,
    color: 'rgba(255,255,255,0.9)',
    fontSize: 11.5,
    fontWeight: 500
  },
  input: {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 41,
    padding: '0 14px',
    border: '0.86px solid rgba(255,255,255,0.1)',
    borderRadius: 8.5,
    background: 'rgba(255,255,255,0.05)',
    color: '#FFFFFF',
    font: 'inherit',
    fontSize: 12
  },
  textarea: {
    minHeight: 89,
    padding: '12px 14px',
    resize: 'vertical'
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: 104,
    height: 35,
    padding: 3,
    gap: 2,
    background: 'rgba(255,255,255,0.05)',
    border: '0.86px solid rgba(255,255,255,0.1)',
    borderRadius: 999
  },
  pillRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  choiceGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, width: '100%' },
  choice: { display: 'flex', alignItems: 'center', gap: 10, color: '#E0DEEB', fontSize: 12, cursor: 'pointer' },
  referralRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    width: '100%'
  },
  referralControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 7
  },
  applyBtn: {
    width: 69,
    minHeight: 38,
    border: '0.86px solid rgba(177,160,254,0.45)',
    borderRadius: 7,
    background: 'rgba(255,255,255,0.08)',
    color: '#B1A0FE',
    fontWeight: 500,
    fontSize: 11,
    cursor: 'pointer'
  },
  uploadBtn: {
    width: '100%',
    minHeight: 41,
    border: '0.86px dashed rgba(177,160,254,0.45)',
    borderRadius: 8.5,
    background: 'rgba(255,255,255,0.05)',
    color: '#B1A0FE',
    fontWeight: 500,
    fontSize: 12,
    cursor: 'pointer'
  },
  removeBtn: {
    border: 0,
    background: 'transparent',
    color: '#BBF7D0',
    fontWeight: 600,
    fontSize: 12,
    cursor: 'pointer'
  },
  appliedBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 38,
    padding: '8px 12px',
    borderRadius: 8.5,
    border: '1px solid #16A34A',
    background: 'rgba(22,163,74,.14)'
  },
  errorText: { marginTop: 8, color: '#FCA5A5', fontSize: 12 },
  hint: { marginTop: 8, color: '#64748B', fontSize: 12 },
  successIcon: {
    width: 44,
    height: 44,
    borderRadius: 999,
    background: '#16A34A',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 16
  },
  referredBy: {
    margin: '8px 0 18px',
    color: '#86EFAC',
    fontSize: 13,
    fontWeight: 600
  },
  steps: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    margin: '8px 0 22px',
    padding: '14px 0',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    borderBottom: '1px solid rgba(255,255,255,0.08)'
  },
  step: { padding: '0 8px' },
  stepNum: {
    fontFamily: TITLE_FONT,
    fontSize: 11,
    letterSpacing: '.14em',
    color: '#A78BFA',
    marginBottom: 6
  },
  shareBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 14,
    borderRadius: 12,
    background: 'rgba(126,95,254,.16)',
    border: '1px solid rgba(177,160,254,.35)',
    marginBottom: 16
  },
  copyBtn: {
    border: 0,
    borderRadius: 8,
    background: BRAND,
    color: '#fff',
    fontWeight: 700,
    padding: '8px 12px',
    cursor: 'pointer'
  },
  doneBtn: {
    width: '100%',
    minHeight: 45,
    border: 0,
    borderRadius: 5,
    background: 'linear-gradient(90deg, #7E5FFE 0%, #512ED0 100%)',
    boxShadow: '0 7px 21px -3px rgba(126,95,254,0.35)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer'
  }
};
